import React, { ChangeEvent, useContext, useState } from 'react';
import { Grid2 as Grid, Card, CardContent, CardHeader, Typography, Stack, Link, Chip, useTheme, alpha, Alert, useMediaQuery, Box, Button, IconButton, FormGroup, FormControlLabel, Switch, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody} from '@mui/material';
import { FilersDataContext } from '../../Filers';
import DocumentViewer from '../DocumentViewer/DocumentViewer';
import EmailIcon from '@mui/icons-material/Email';
import FilerAuditLetter from '../../../../types/filers/filerAuditLetter';
import FilerAuditResponse from '../../../../types/filers/filerAuditResponse';

const FilerAudit = (): JSX.Element => {

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const [isTableView, setIsTableView] = useState(isSM? false: true);
    const filerDataContext = useContext(FilersDataContext);
    const filerAuditIssues = filerDataContext.currentFilerAuditIssues;
    const excelExportUrl =
      filerDataContext.currentFilerPayload
        ? `${process.env.REACT_APP_API_BASE_URL}/filer/issues/export/${filerDataContext.currentFilerPayload.filer.cpfId}`
        : '';

    const [isDocumentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [selectedLetter, setSelectedLetter] = useState<FilerAuditLetter>();
    const [selectedResponse, setSelectedResponse] = useState<FilerAuditResponse>();
    
    const openDocumentViewerByLetter = (letter: FilerAuditLetter) => {
        setDocumentViewerOpen(true);
        setSelectedLetter(letter);
      };
    
      const openDocumentViewerByResponse = (response: FilerAuditResponse) => {
        setDocumentViewerOpen(true);
        setSelectedResponse(response);
      };

      const closeDocumentViewer = () => {
        setDocumentViewerOpen(false);
        setSelectedLetter(undefined);  
        setSelectedResponse(undefined); 
      };

      const getIssueResponseDisplay = (response: FilerAuditResponse) => {
          return (
            <>
             {
                response.responseFormat === "Letter/Email" && response.blobUrl &&
                  <Link>
                    <Typography component={'i'} className="is-clickable" variant="body2" onClick={()=>openDocumentViewerByResponse(response)}>
                      {response.responseFormat}
                    </Typography>
                  </Link>
            }
             {
                  response.responseFormat === "Letter/Email" && !response.blobUrl &&
                  <Chip label={response.description} size="small" color="primary" variant="outlined"/>
             }                 
            {
                  response.responseFormat === "Report" &&
                  <Link href={`/reports/displayreport?id=${response.reportId}`} target="_blank">
                    <Typography component={'i'} className="is-clickable" variant="body2">
                      {response.linkDescription}
                    </Typography>
                  </Link>
            }
            </>
          );

      }

      const handleViewChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setIsTableView(checked);
      }

    return (
        <Grid container marginTop={0} marginBottom={2}  spacing={4}>
          {
            filerAuditIssues.length === 0 &&
             <Grid size={12} marginBottom={1} marginTop={1}>
                  <Alert icon={false} severity="info"
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        maxWidth: '95%',
                        '.MuiAlert-message': { 
                          padding: '4px',
                        } 
                      }}
                    >
                    No Audit Issues Found.
                  </Alert>
              </Grid>
          }
          {
            filerAuditIssues.length > 0 &&
             <Grid size={11} marginBottom={-1} marginTop={-1}>
              <Stack direction="row" spacing={0.1} justifyContent={isSM?'flex-start':'flex-end'} alignItems={'center'} flexWrap={'wrap'}>
                 <FormGroup>
                    <FormControlLabel 
                      control={<Switch checked={isTableView} onChange={handleViewChange} />} 
                      label="Table View" 
                      sx={{ 
                        '& .MuiFormControlLabel-label': { fontSize: '0.9rem', fontStyle: 'italic'} 
                        }}
                      />
                 </FormGroup>
                 <Button 
                    component={Link} 
                    underline="none" 
                    variant="text"
                    onClick={() => window.open(excelExportUrl)} 
                    startIcon={
                    <IconButton aria-label="export to excel" size="small" color="success">
                      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 15 15"><path fill="currentColor" d="M4.5 6.995H4v1h.5zm6 1h.5v-1h-.5zm-6 2.505H4v.5h.5zm6 0v.5h.5v-.5zm-6-6.503H4v1h.5zm6 1h.5v-1h-.5zm3-1.497h.5v-.207l-.146-.147zm-3-3l.354-.354L10.707 0H10.5zm-6 7.495h6v-1h-6zM4.5 11h6v-1h-6zm0-6.003h6v-1h-6zm8 9.003h-10v1h10zM2 13.5v-12H1v12zm11-10v10h1v-10zM2.5 1h8V0h-8zm7.646-.146l3 3l.708-.708l-3-3zM2.5 14a.5.5 0 0 1-.5-.5H1A1.5 1.5 0 0 0 2.5 15zm10 1a1.5 1.5 0 0 0 1.5-1.5h-1a.5.5 0 0 1-.5.5zM2 1.5a.5.5 0 0 1 .5-.5V0A1.5 1.5 0 0 0 1 1.5zm2 3v6h1v-6zm3 0v6h1v-6zm3 0v6h1v-6z"/></svg>
                    </IconButton>
                    } 
                    color="success">
                    Export Issue List
                 </Button>
              </Stack>
          </Grid>
          }

          {/* Table View */}
          {
            isTableView && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{minWidth: 200}}><Typography variant='subtitle2' fontWeight={550}>Audit Issue</Typography></TableCell>
                      <TableCell sx={{minWidth: 200}}><Typography variant='subtitle2' fontWeight={550}>Audit Letter Sent</Typography></TableCell>
                      <TableCell><Typography variant='subtitle2' fontWeight={550}>Issue Status</Typography></TableCell>
                      <TableCell><Typography variant='subtitle2' fontWeight={550}>Issue Responses</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filerAuditIssues.map((issue) => (
                      <TableRow key={issue.id}>
                        <TableCell><Typography variant="body2">{issue.dateDisplay} {issue.description}</Typography></TableCell>
                        <TableCell>
                          {issue.sentLetters.map((letter, index) => (
                              <Stack direction={"row"} spacing={0.1} key={index}>
                                  <EmailIcon fontSize="small" color="action" />
                                  <Link underline='hover'>
                                    <Typography component={'i'} className="is-clickable" variant="body2" onClick={()=>openDocumentViewerByLetter(letter)}>
                                        {letter.dateSentDisplay} {letter.letterType}
                                    </Typography>
                                  </Link>
                              </Stack>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Typography
                          variant={'caption'}
                          fontWeight={700}
                          sx={{
                            bgcolor: issue.status === "Outstanding"? alpha(theme.palette.error.light, 0.1): alpha(theme.palette.success.light, 0.1),
                            color: issue.status === "Outstanding"? theme.palette.error.dark: theme.palette.success.dark,
                            paddingX: 1.5,
                            paddingY: 0.5,
                            borderRadius: 4,
                            display: 'inline',
                            }}
                          >
                          {issue.status}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} marginTop={0.5} useFlexGap flexWrap="wrap">
                              {issue.responses.length === 0 ? (
                                  <Chip label="no responses yet" size="small" color="warning" variant="outlined"/>
                                  ) : (
                                    issue.responses.map((response, index) => (
                                    <React.Fragment key={index}>
                                    {getIssueResponseDisplay(response)}
                                    </React.Fragment>
                                    ))
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          }

          {/* Card View */}
          {!isTableView && filerAuditIssues.map((issue) => (
            <Grid size={{xs:12, md:4, lg: 3.6, sm:6}} key={issue.id}>
              <Card 
                variant="outlined"
                sx={{ height: '100%' }}
                >
                <CardHeader
                    title={
                      <Stack direction="row" spacing={1}>
                        <Typography variant={'subtitle2'} fontWeight={550}>
                          ISSUE:
                        </Typography>
                        <Typography variant={'subtitle2'}>
                            {issue.dateDisplay} {issue.description}
                        </Typography>
                    </Stack>
                    }
                    sx={{backgroundColor: "#f5f5f5", padding: "8px"}}
                />
                <CardContent className="card-content">
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Typography variant="subtitle2" paddingTop={"3px"} fontWeight={550}>
                            Audit Letters:
                        </Typography>
                        {
                            issue.sentLetters.map((letter, index) => (
                                <Stack direction={"row"} spacing={0.1} key={index}>
                                  <EmailIcon fontSize="small" color="action" />
                                  <Link underline='hover'>
                                    <Typography component={'i'} className="is-clickable" variant="body2" onClick={()=>openDocumentViewerByLetter(letter)}>
                                        {letter.dateSentDisplay} {letter.letterType}
                                    </Typography>
                                  </Link>
                                </Stack>
                            ))
                        }
                    </Stack>
                    <Stack direction="row" spacing={1} marginTop={0.5} useFlexGap flexWrap="wrap">
                        <Typography variant="subtitle2" paddingTop={"3px"} fontWeight={550}>
                            Responses:
                        </Typography>
                          {
                            issue.responses.length === 0 &&
                            <Chip label="no responses yet" size="small" color="warning" variant="outlined"/>
                          }
                          {
                            issue.responses.length >0 &&
                            issue.responses.map((response, index) => (
                              <React.Fragment key={index}>
                                {getIssueResponseDisplay(response)}
                              </React.Fragment>
                            ))
                        }
                    </Stack>
                    <Stack direction="row" spacing={1} marginTop={0.5}>
                        <Typography variant="subtitle2" paddingTop={"3px"} fontWeight={550}>
                            Status:
                        </Typography>
                        <Typography
                          variant={'caption'}
                          fontWeight={700}
                          sx={{
                            bgcolor: issue.status === "Outstanding"? alpha(theme.palette.error.light, 0.1): alpha(theme.palette.success.light, 0.1),
                            color: issue.status === "Outstanding"? theme.palette.error.dark: theme.palette.success.dark,
                            paddingX: 1.5,
                            paddingY: 0.5,
                            borderRadius: 4,
                            display: 'inline',
                            }}
                          >
                          {issue.status}
                          </Typography>
                    </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <DocumentViewer isDocumentViewerOpen={isDocumentViewerOpen} closeDocumentViewer={closeDocumentViewer} letter={selectedLetter} response={selectedResponse}></DocumentViewer>
        </Grid>
    );
  };

export default FilerAudit;